import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import {
  Building2, Loader2, Play, Download, ExternalLink, Crosshair, Network, Gavel, Lightbulb,
  ShieldAlert, Truck, Cpu, Handshake, Users, TrendingUp, Layers, AlertTriangle,
} from "lucide-react";
import { runOrgDossier, type OrgDossier, type OrgItem } from "@/lib/intel/feeds.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { SeverityBadge } from "@/components/intel/SeverityBadge";

const KIND_TONE: Record<string, string> = {
  parent: "border-primary/50 text-primary",
  subject: "border-cyan/60 text-cyan",
  subsidiary: "border-success/50 text-success",
  shareholder: "border-amber/50 text-amber",
  affiliate: "border-panel-border text-muted-foreground",
};

function Sources({ urls }: { urls: string[] }) {
  if (!urls?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {urls.map((u, i) => (
        <a key={u + i} href={u} target="_blank" rel="noreferrer noopener"
          className="font-mono text-[10px] text-muted-foreground hover:text-cyan">[{i + 1}]</a>
      ))}
    </div>
  );
}

function ItemSection({
  icon: Icon, title, items, empty,
}: { icon: typeof Gavel; title: string; items: OrgItem[]; empty: string }) {
  return (
    <div className="glass rounded-lg p-4">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
        <Icon className="h-3 w-3" /> {title} · {items.length}
      </div>
      {items.length === 0 ? (
        <div className="mt-2 text-xs text-muted-foreground">{empty}</div>
      ) : (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {items.map((i, idx) => (
            <div key={i.title + idx} className="rounded-md border border-panel-border bg-background/40 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{i.title}</span>
                {i.status && (
                  <span className="rounded bg-panel/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    {i.status}
                  </span>
                )}
              </div>
              {i.date && <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{i.date}</div>}
              {i.detail && <div className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{i.detail}</div>}
              <Sources urls={i.sources} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function toMarkdown(d: OrgDossier): string {
  const sec = (t: string, items: OrgItem[]) =>
    items.length ? `\n## ${t}\n` + items.map((i) => `- **${i.title}**${i.date ? ` (${i.date})` : ""}${i.status ? ` — ${i.status}` : ""}${i.detail ? `\n  ${i.detail}` : ""}${i.sources.length ? `\n  Sources: ${i.sources.join(" ")}` : ""}`).join("\n") : "";
  return [
    `# Company dossier — ${d.company.legal_name}`,
    `Generated ${new Date(d.generatedAt).toUTCString()} · CARIO NEXUS`,
    "",
    d.company.summary,
    "",
    `- HQ: ${d.company.hq || "—"}`,
    `- Founded: ${d.company.founded || "—"}`,
    `- Industry: ${d.company.industry || "—"}`,
    `- Size: ${d.company.size || "—"}`,
    `- Ownership: ${d.company.ownership || "—"}`,
    d.company.identifiers.length ? `- Identifiers: ${d.company.identifiers.join(", ")}` : "",
    d.company.domains.length ? `- Domains: ${d.company.domains.join(", ")}` : "",
    d.ownership_graph.nodes.length
      ? "\n## Ownership graph\n" + d.ownership_graph.nodes.map((n) => `- [${n.kind}] ${n.name}${n.stake ? ` — ${n.stake}` : ""}${n.jurisdiction ? ` (${n.jurisdiction})` : ""}`).join("\n") +
        (d.ownership_graph.edges.length ? "\n\nRelations:\n" + d.ownership_graph.edges.map((e) => {
          const f = d.ownership_graph.nodes.find((n) => n.id === e.from)?.name ?? e.from;
          const t = d.ownership_graph.nodes.find((n) => n.id === e.to)?.name ?? e.to;
          return `- ${f} → ${t}${e.label ? ` (${e.label})` : ""}`;
        }).join("\n") : "")
      : "",
    d.executives.length ? "\n## Executives\n" + d.executives.map((p) => `- **${p.name}** — ${p.role}${p.background ? `\n  ${p.background}` : ""}${p.risk_notes ? `\n  Risk: ${p.risk_notes}` : ""}`).join("\n") : "",
    sec("Subsidiaries", d.subsidiaries),
    sec("Lawsuits", d.lawsuits),
    sec("Patents", d.patents),
    sec("Sanctions", d.sanctions),
    sec("Suppliers", d.suppliers),
    sec("Technologies", d.technologies),
    sec("Acquisitions", d.acquisitions),
    d.hiring_trends.summary || d.hiring_trends.signals.length
      ? `\n## Hiring trends\n${d.hiring_trends.summary}\n` + d.hiring_trends.signals.map((s) => `- ${s.title}${s.detail ? ` — ${s.detail}` : ""}`).join("\n")
      : "",
    d.risk_flags.length ? "\n## Risk flags\n" + d.risk_flags.map((r) => `- [${r.severity.toUpperCase()}] ${r.label}${r.detail ? ` — ${r.detail}` : ""}`).join("\n") : "",
    d.citations.length ? "\n## Sources\n" + d.citations.map((c) => `- ${c}`).join("\n") : "",
  ].filter(Boolean).join("\n");
}

export function OrgDossierPanel({ company, onPivot }: { company: string; onPivot?: (t: string) => void }) {
  const run = useServerFn(runOrgDossier);
  const [name, setName] = useState(company);
  const [geography, setGeography] = useState("");
  const [industry, setIndustry] = useState("");
  const [data, setData] = useState<OrgDossier | null>(null);

  const m = useMutation({
    mutationFn: async () =>
      run({
        data: {
          company: name.trim(),
          environment: getPaddleEnvironment(),
          ...(geography.trim() ? { geography: geography.trim() } : {}),
          ...(industry.trim() ? { industry: industry.trim() } : {}),
        },
      }),
    onSuccess: (d) => setData(d),
  });

  function download() {
    if (!data) return;
    const blob = new Blob([toMarkdown(data)], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dossier-${data.company.legal_name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3">
      <div className="glass rounded-lg p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex w-full flex-1 items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2 sm:w-auto sm:min-w-[220px]">
            <Building2 className="h-4 w-4 text-cyan" />
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && m.mutate()}
              placeholder="Company name or domain…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <button
            onClick={() => name.trim() && m.mutate()}
            disabled={m.isPending || !name.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {m.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {m.isPending ? "Building dossier…" : "Generate dossier"}
          </button>
          {data && (
            <button onClick={download}
              className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm hover:border-cyan/40">
              <Download className="h-3.5 w-3.5" /> Export
            </button>
          )}
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-cyan/50" />
          <input value={geography} onChange={(e) => setGeography(e.target.value)} placeholder="Geography (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-cyan/50" />
        </div>
        {m.isError && (
          <div className="mt-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
            {m.error instanceof Error ? m.error.message : "Dossier generation failed"}
          </div>
        )}
      </div>

      {!data && !m.isPending && (
        <div className="rounded-md border border-panel-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
          One click builds the full corporate dossier: ownership graph, subsidiaries, executives, lawsuits, patents,
          sanctions, suppliers, technologies, acquisitions and hiring trends.
        </div>
      )}

      {data && (
        <>
          <div className="glass rounded-lg p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Company dossier</div>
            <div className="mt-1 font-display text-xl">{data.company.legal_name}</div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{data.company.summary || "No summary returned."}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {([
                ["HQ", data.company.hq],
                ["Founded", data.company.founded],
                ["Industry", data.company.industry],
                ["Size", data.company.size],
                ["Ownership", data.company.ownership],
                ["Identifiers", data.company.identifiers.join(", ")],
                ["Domains", data.company.domains.join(", ")],
              ] as Array<[string, string]>).filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="mt-0.5 break-words text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {data.ownership_graph.nodes.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
                <Network className="h-3 w-3" /> Ownership graph · {data.ownership_graph.nodes.length} entities
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.ownership_graph.nodes.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => onPivot?.(n.name)}
                    className={`rounded-md border bg-background/40 px-2.5 py-1.5 text-left hover:bg-panel/50 ${KIND_TONE[n.kind] ?? KIND_TONE.affiliate}`}
                  >
                    <div className="font-mono text-[9px] uppercase tracking-widest opacity-80">{n.kind}{n.stake ? ` · ${n.stake}` : ""}</div>
                    <div className="text-sm text-foreground">{n.name}</div>
                    {n.jurisdiction && <div className="font-mono text-[10px] text-muted-foreground">{n.jurisdiction}</div>}
                  </button>
                ))}
              </div>
              {data.ownership_graph.edges.length > 0 && (
                <ul className="mt-3 space-y-1 font-mono text-[11px] text-muted-foreground">
                  {data.ownership_graph.edges.map((e, i) => {
                    const f = data.ownership_graph.nodes.find((n) => n.id === e.from)?.name ?? e.from;
                    const t = data.ownership_graph.nodes.find((n) => n.id === e.to)?.name ?? e.to;
                    return <li key={i}>{f} <span className="text-cyan">→</span> {t}{e.label ? ` · ${e.label}` : ""}</li>;
                  })}
                </ul>
              )}
            </div>
          )}

          {data.executives.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
                <Users className="h-3 w-3" /> Executives & board · {data.executives.length}
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {data.executives.map((p) => (
                  <div key={p.name + p.role} className="rounded-md border border-panel-border bg-background/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{p.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{p.role}</div>
                      </div>
                      {onPivot && (
                        <button onClick={() => onPivot(p.name)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan/40 bg-cyan/10 px-2 py-1 font-mono text-[10px] text-cyan hover:bg-cyan/20">
                          <Crosshair className="h-3 w-3" /> Investigate
                        </button>
                      )}
                    </div>
                    {p.background && <div className="mt-2 text-xs text-muted-foreground">{p.background}</div>}
                    {p.risk_notes && (
                      <div className="mt-1.5 flex gap-1.5 text-xs text-amber">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {p.risk_notes}
                      </div>
                    )}
                    <Sources urls={p.sources} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <ItemSection icon={Layers} title="Subsidiaries" items={data.subsidiaries} empty="No subsidiaries found in public records." />
          <ItemSection icon={Gavel} title="Lawsuits & litigation" items={data.lawsuits} empty="No litigation found in public dockets." />
          <ItemSection icon={Lightbulb} title="Patents & IP" items={data.patents} empty="No patents found." />
          <ItemSection icon={ShieldAlert} title="Sanctions & watchlists" items={data.sanctions} empty="No sanctions or watchlist matches." />
          <ItemSection icon={Truck} title="Suppliers & partners" items={data.suppliers} empty="No supplier relationships disclosed." />
          <ItemSection icon={Cpu} title="Technology stack" items={data.technologies} empty="No technology signals detected." />
          <ItemSection icon={Handshake} title="Acquisitions & deals" items={data.acquisitions} empty="No M&A activity found." />

          <div className="glass rounded-lg p-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
              <TrendingUp className="h-3 w-3" /> Hiring trends
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {data.hiring_trends.summary || "No hiring signals found."}
            </p>
            {data.hiring_trends.signals.length > 0 && (
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {data.hiring_trends.signals.map((s, i) => (
                  <div key={s.title + i} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                    <div className="text-sm font-medium">{s.title}</div>
                    {s.detail && <div className="mt-1 text-xs text-muted-foreground">{s.detail}</div>}
                    <Sources urls={s.sources} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {data.risk_flags.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Risk flags</div>
              <div className="mt-3 space-y-2">
                {data.risk_flags.map((r) => (
                  <div key={r.label} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                    <div className="flex items-center gap-2">
                      <SeverityBadge s={r.severity} />
                      <span className="text-sm font-medium">{r.label}</span>
                    </div>
                    {r.detail && <div className="mt-1 text-xs text-muted-foreground">{r.detail}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.citations.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Sources</div>
              <div className="mt-2 space-y-1">
                {data.citations.map((c) => (
                  <a key={c} href={c} target="_blank" rel="noreferrer noopener"
                    className="flex items-center gap-1.5 truncate font-mono text-[11px] text-muted-foreground hover:text-cyan">
                    <ExternalLink className="h-3 w-3 shrink-0" /> {c}
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
